import { useState } from "react";

import { AlertTriangle, ExternalLink, FileText } from "lucide-react";
import PropTypes from "prop-types";
import classnames from "classnames";
import LoadingButton from "@components/ui-components/loading-button";
import { formatFileSize } from "@panel/shared/contract/misc/helpers";
import { formatDate } from "@helpers/date";
import { INVOICE_DOCUMENT_LABEL, INVOICE_PREVIEW_KIND } from "../misc/constants";
import { invoicePreviewKind } from "../misc/helpers";

const FRAME_CLASS = "flex items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-light/60";

const Tile = ({ icon: Icon, message }) => (
    <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
        <span className="grid h-10 w-10 place-items-center rounded-lg border border-gray-200 bg-white text-primary">
            <Icon className="h-5 w-5" />
        </span>
        <p className="max-w-xs text-xs leading-relaxed text-muted">{message}</p>
    </div>
);

Tile.propTypes = {
    icon   : PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    message: PropTypes.string.isRequired
};

/**
 * The tax invoice attached to a payout invoice, read-only: the stored file
 * drawn in place, with the link out as the fallback for anything the browser
 * will not render. Uploading and replacing stay in the publisher portal — this
 * is only ever how the document is read back.
 */
const InvoiceDocument = ({ doc, className }) => {
    const [failed, setFailed] = useState(false);
    const kind = invoicePreviewKind(doc);

    return (
        <div className={classnames("rounded-lg border border-gray-200 p-3", className)} data-cy="invoice-document">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase text-muted">{INVOICE_DOCUMENT_LABEL.TITLE}</p>
                    <p className="mt-1 truncate font-medium text-gray-900" title={doc?.name}>
                        {doc?.name || "—"}
                    </p>
                    {doc && (
                        <p className="mt-0.5 text-xs text-muted">
                            {formatFileSize(doc.size)}
                            {doc.uploadedAt && ` · uploaded ${formatDate(doc.uploadedAt)}`}
                        </p>
                    )}
                </div>

                {doc && (
                    <LoadingButton
                        color="neutral"
                        size="sm"
                        icon={ExternalLink}
                        text={INVOICE_DOCUMENT_LABEL.OPEN}
                        href={doc.url}
                        target="_blank"
                        testId="open-invoice-document" />
                )}
            </div>

            <div className={classnames(FRAME_CLASS, "mt-3")}>
                {!doc && <Tile icon={FileText} message={INVOICE_DOCUMENT_LABEL.MISSING} />}

                {doc && kind === INVOICE_PREVIEW_KIND.IMAGE && !failed && (
                    <img
                        src={doc.url}
                        alt={`Tax invoice ${doc.name}`}
                        onError={() => setFailed(true)}
                        referrerPolicy="no-referrer"
                        className="max-h-[320px] w-auto bg-white" />
                )}

                {/* No `sandbox`: Chrome and Firefox run their PDF viewer as a
                    plugin document, which a sandboxed frame is not allowed to
                    instantiate. */}
                {doc && kind === INVOICE_PREVIEW_KIND.PDF && (
                    <iframe
                        src={doc.url}
                        title={`Tax invoice ${doc.name}`}
                        referrerPolicy="no-referrer"
                        className="h-[320px] w-full bg-white" />
                )}

                {doc && (kind === INVOICE_PREVIEW_KIND.NONE || failed) && (
                    <Tile icon={AlertTriangle} message={INVOICE_DOCUMENT_LABEL.FAILED} />
                )}
            </div>
        </div>
    );
};

InvoiceDocument.propTypes = {
    doc: PropTypes.shape({
        url       : PropTypes.string,
        name      : PropTypes.string,
        size      : PropTypes.number,
        uploadedAt: PropTypes.string
    }),
    className: PropTypes.string
};

export default InvoiceDocument;
